import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiUrl } from '../config/api';

// ============================================================
// BRAINFORGE COMPREHENSIVE ADMIN DASHBOARD
// Full-featured SaaS analytics UI
// ============================================================

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    color: '#e2e8f0',
    fontFamily: "'Inter', 'DM Sans', sans-serif",
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    margin: 0,
    background: 'linear-gradient(135deg, #60a5fa, #a78bfa)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  controls: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  select: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    fontSize: '14px',
    cursor: 'pointer',
    outline: 'none',
  },
  button: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#3b82f6',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  buttonSecondary: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    flexWrap: 'wrap',
    borderBottom: '1px solid #334155',
    paddingBottom: '8px',
  },
  tab: {
    padding: '12px 24px',
    borderRadius: '8px 8px 0 0',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontWeight: '500',
  },
  tabActive: {
    backgroundColor: '#3b82f6',
    color: 'white',
  },
  content: {
    maxWidth: '1600px',
    margin: '0 auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '24px',
  },
  gridWide: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '20px',
    marginBottom: '24px',
  },
  card: {
    backgroundColor: 'rgba(30, 41, 59, 0.8)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #334155',
    backdropFilter: 'blur(10px)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#e2e8f0',
    margin: 0,
  },
  cardSubtitle: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: 'normal',
  },
  metricCard: {
    backgroundColor: 'rgba(30, 41, 59, 0.8)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #334155',
    position: 'relative',
    overflow: 'hidden',
  },
  metricValue: {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  metricLabel: {
    fontSize: '14px',
    color: '#94a3b8',
  },
  metricChange: {
    fontSize: '12px',
    marginTop: '8px',
  },
  metricIcon: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    fontSize: '32px',
    opacity: 0.2,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    borderBottom: '1px solid #334155',
    color: '#94a3b8',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid #1e293b',
    fontSize: '14px',
  },
  clickableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  badgeSuccess: {
    backgroundColor: 'rgba(34, 197, 94, 0.2)',
    color: '#22c55e',
  },
  badgeWarning: {
    backgroundColor: 'rgba(245, 158, 11, 0.2)',
    color: '#f59e0b',
  },
  badgeDanger: {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    color: '#ef4444',
  },
  badgeInfo: {
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    color: '#3b82f6',
  },
  progressBar: {
    height: '8px',
    backgroundColor: '#1e293b',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    backgroundColor: '#1e293b',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '900px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
    border: '1px solid #334155',
  },
  modalClose: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    fontSize: '24px',
    cursor: 'pointer',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    fontSize: '18px',
    color: '#94a3b8',
  },
  error: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    padding: '16px',
    color: '#ef4444',
    marginBottom: '20px',
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#22c55e',
    fontSize: '14px',
  },
  liveDot: {
    width: '8px',
    height: '8px',
    backgroundColor: '#22c55e',
    borderRadius: '50%',
    animation: 'pulse 2s infinite',
  },
  searchInput: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #334155',
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    fontSize: '14px',
    width: '250px',
    outline: 'none',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '20px',
  },
  pageButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #334155',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
  },
  pageButtonActive: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
    color: 'white',
  },
  chartContainer: {
    height: '300px',
    display: 'flex',
    alignItems: 'flex-end',
    gap: '4px',
    padding: '20px 0',
  },
  chartBar: {
    flex: 1,
    backgroundColor: '#3b82f6',
    borderRadius: '4px 4px 0 0',
    transition: 'height 0.3s',
    minWidth: '20px',
    maxWidth: '60px',
  },
  chartLabel: {
    fontSize: '10px',
    color: '#64748b',
    textAlign: 'center',
    marginTop: '8px',
    transform: 'rotate(-45deg)',
    transformOrigin: 'top left',
  },
  worldMap: {
    width: '100%',
    height: '300px',
    backgroundColor: '#0f172a',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
  },
  timeline: {
    position: 'relative',
    paddingLeft: '30px',
  },
  timelineItem: {
    position: 'relative',
    paddingBottom: '20px',
    borderLeft: '2px solid #334155',
    paddingLeft: '20px',
  },
  timelineDot: {
    position: 'absolute',
    left: '-7px',
    top: '0',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    border: '2px solid #1e293b',
  },
  exportMenu: {
    position: 'relative',
  },
  exportDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    backgroundColor: '#1e293b',
    borderRadius: '8px',
    border: '1px solid #334155',
    padding: '8px 0',
    minWidth: '200px',
    zIndex: 100,
    marginTop: '8px',
  },
  exportOption: {
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};

// ============================================================
// COMPONENTS
// ============================================================

// Metric Card Component
const MetricCard = ({ icon, label, value, change, color = '#3b82f6', subValue }) => (
  <div style={{ ...styles.metricCard, borderLeft: `4px solid ${color}` }}>
    <div style={{ ...styles.metricIcon }}>{icon}</div>
    <div style={{ ...styles.metricValue, color }}>{value}</div>
    <div style={styles.metricLabel}>{label}</div>
    {subValue && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{subValue}</div>}
    {change !== undefined && (
      <div style={{ 
        ...styles.metricChange, 
        color: change >= 0 ? '#22c55e' : '#ef4444' 
      }}>
        {change >= 0 ? '↑' : '↓'} {Math.abs(change)}% vs previous period
      </div>
    )}
  </div>
);

// Progress Bar Component
const ProgressBar = ({ value, max, color = '#3b82f6', label }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '13px' }}>{label}</span>
        <span style={{ fontSize: '13px', color: '#64748b' }}>{value.toLocaleString()} ({percentage.toFixed(1)}%)</span>
      </div>
      <div style={styles.progressBar}>
        <div style={{ ...styles.progressFill, width: `${percentage}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

// Simple Bar Chart Component
const BarChart = ({ data, labelKey, valueKey, color = '#3b82f6' }) => {
  if (!data || data.length === 0) return <div style={{ color: '#64748b', padding: '20px' }}>No data</div>;
  
  const maxValue = Math.max(...data.map(d => d[valueKey] || 0));
  
  return (
    <div>
      <div style={styles.chartContainer}>
        {data.slice(0, 14).map((item, index) => (
          <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div 
              style={{ 
                ...styles.chartBar, 
                height: `${maxValue > 0 ? ((item[valueKey] || 0) / maxValue) * 250 : 0}px`,
                backgroundColor: color,
                minHeight: '4px'
              }}
              title={`${item[labelKey]}: ${item[valueKey]}`}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#64748b' }}>
        <span>{data[0]?.[labelKey]}</span>
        <span>{data[data.length - 1]?.[labelKey]}</span>
      </div>
    </div>
  );
};

// Badge Component
const Badge = ({ type, children }) => {
  const typeStyles = {
    success: styles.badgeSuccess,
    warning: styles.badgeWarning,
    danger: styles.badgeDanger,
    info: styles.badgeInfo,
  };
  return <span style={{ ...styles.badge, ...typeStyles[type] }}>{children}</span>;
};

// Pagination Component
const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  
  const pages = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) {
    pages.push(i);
  }
  
  return (
    <div style={styles.pagination}>
      <button 
        style={styles.pageButton} 
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1}
      >
        ← Prev
      </button>
      {pages.map(p => (
        <button
          key={p}
          style={{ ...styles.pageButton, ...(p === page ? styles.pageButtonActive : {}) }}
          onClick={() => onPageChange(p)}
        >
          {p}
        </button>
      ))}
      <button 
        style={styles.pageButton} 
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages}
      >
        Next →
      </button>
    </div>
  );
};

// ============================================================
// MAIN DASHBOARD COMPONENT
// ============================================================
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [usersData, setUsersData] = useState({ users: [], total: 0, page: 1 });
  const [sessionsData, setSessionsData] = useState({ sessions: [], total: 0, page: 1 });
  const [aiUsageData, setAiUsageData] = useState(null);
  const [errorsData, setErrorsData] = useState(null);
  const [searchesData, setSearchesData] = useState(null);
  
  // Modal states
  const [drillModal, setDrillModal] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  
  // Search/filter states
  const [userSearch, setUserSearch] = useState('');
  const [sessionFilter, setSessionFilter] = useState('');

  // ============================================================
  // DATA FETCHING
  // ============================================================
  
  const fetchWithAuth = useCallback(async (endpoint) => {
    const token = localStorage.getItem('token');
    const response = await fetch(apiUrl(endpoint), {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.status === 403) {
      throw new Error('Admin access required');
    }
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    return response.json();
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth(`/api/admin/dashboard?days=${dateRange}`);
      setDashboardData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, fetchWithAuth]);

  const fetchRealtime = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/api/admin/realtime');
      setRealtimeData(data);
    } catch (err) {
      console.error('Realtime fetch error:', err);
    }
  }, [fetchWithAuth]);

  const fetchGeo = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`/api/admin/geo?days=${dateRange}`);
      setGeoData(data);
    } catch (err) {
      console.error('Geo fetch error:', err);
    }
  }, [dateRange, fetchWithAuth]);

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    try {
      const data = await fetchWithAuth(`/api/admin/users?page=${page}&search=${search}`);
      setUsersData(data);
    } catch (err) {
      console.error('Users fetch error:', err);
    }
  }, [fetchWithAuth]);

  const fetchSessions = useCallback(async (page = 1, filter = '') => {
    try {
      const endpoint = `/api/admin/sessions?page=${page}${filter ? `&filter=${filter}` : ''}`;
      const data = await fetchWithAuth(endpoint);
      setSessionsData(data);
    } catch (err) {
      console.error('Sessions fetch error:', err);
    }
  }, [fetchWithAuth]);

  const fetchAiUsage = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`/api/admin/ai-usage?days=${dateRange}`);
      setAiUsageData(data);
    } catch (err) {
      console.error('AI usage fetch error:', err);
    }
  }, [dateRange, fetchWithAuth]);

  const fetchErrors = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`/api/admin/errors?days=${dateRange}`);
      setErrorsData(data);
    } catch (err) {
      console.error('Errors fetch error:', err);
    }
  }, [dateRange, fetchWithAuth]);

  const fetchSearches = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`/api/admin/searches?days=${dateRange}`);
      setSearchesData(data);
    } catch (err) {
      console.error('Searches fetch error:', err);
    }
  }, [dateRange, fetchWithAuth]);

  // Drill-down fetch
  const fetchDrillDown = async (type, params) => {
    try {
      let endpoint;
      switch (type) {
        case 'country':
          endpoint = `/api/admin/geo/country/${encodeURIComponent(params.code)}?days=${dateRange}`;
          break;
        case 'user':
          endpoint = `/api/admin/users/${params.userId}`;
          break;
        case 'session':
          endpoint = `/api/admin/sessions/${params.sessionId}`;
          break;
        default:
          return;
      }
      
      const data = await fetchWithAuth(endpoint);
      setDrillData(data);
      setDrillModal(type);
    } catch (err) {
      console.error('Drill-down error:', err);
    }
  };

  // Export function
  const handleExport = async (type, format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        apiUrl(`/api/admin/export?type=${type}&format=${format}`),
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (format === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `brainforge_${type}_${Date.now()}.csv`;
        a.click();
      } else {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `brainforge_${type}_${Date.now()}.json`;
        a.click();
      }
      
      setExportMenuOpen(false);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  // ============================================================
  // EFFECTS
  // ============================================================

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchDashboard();
  }, [user, navigate, fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'realtime') {
      fetchRealtime();
      const interval = setInterval(fetchRealtime, 10000); // Every 10 seconds
      return () => clearInterval(interval);
    }
  }, [activeTab, fetchRealtime]);

  useEffect(() => {
    if (activeTab === 'geography') fetchGeo();
    if (activeTab === 'users') fetchUsers(1, userSearch);
    if (activeTab === 'sessions') fetchSessions(1, sessionFilter);
    if (activeTab === 'ai') fetchAiUsage();
    if (activeTab === 'errors') fetchErrors();
    if (activeTab === 'searches') fetchSearches();
  }, [activeTab, dateRange, fetchGeo, fetchUsers, fetchSessions, fetchAiUsage, fetchErrors, fetchSearches, userSearch, sessionFilter]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchDashboard, 60000); // Every minute
      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchDashboard]);

  // ============================================================
  // RENDER FUNCTIONS
  // ============================================================

  const renderOverview = () => {
    if (!dashboardData) return null;
    
    const d = dashboardData;
    
    return (
      <>
        {/* Key Metrics */}
        <div style={styles.grid}>
          <MetricCard 
            icon="👥" 
            label="Total Visitors" 
            value={d.uniqueVisitors?.toLocaleString() || '0'}
            subValue={`${d.totalSessions?.toLocaleString() || '0'} sessions`}
            color="#3b82f6" 
          />
          <MetricCard 
            icon="📄" 
            label="Page Views" 
            value={d.totalPageViews?.toLocaleString() || '0'}
            subValue={`${d.pagesPerSession || '0'} per session`}
            color="#10b981" 
          />
          <MetricCard 
            icon="⏱️" 
            label="Avg. Session" 
            value={d.avgSessionDurationFormatted || '0s'}
            color="#f59e0b" 
          />
          <MetricCard 
            icon="📈" 
            label="Bounce Rate" 
            value={`${d.bounceRate || '0'}%`}
            color="#ef4444" 
          />
          <MetricCard 
            icon="💬" 
            label="Conversations" 
            value={d.totalConversations?.toLocaleString() || '0'}
            color="#8b5cf6" 
          />
          <MetricCard 
            icon="👤" 
            label="Registered Users" 
            value={d.totalRegisteredUsers?.toLocaleString() || '0'}
            color="#06b6d4" 
          />
        </div>

        <div style={styles.gridWide}>
          {/* Daily Trend Chart */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📊 Traffic Trend</h3>
            <BarChart 
              data={d.dailyTrend || []} 
              labelKey="day" 
              valueKey="count" 
              color="#3b82f6"
            />
          </div>

          {/* Traffic Sources */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🔗 Traffic Sources</h3>
            <div style={{ marginTop: '20px' }}>
              <ProgressBar 
                label="Direct" 
                value={d.trafficSources?.direct || 0} 
                max={d.totalSessions || 1}
                color="#3b82f6"
              />
              <ProgressBar 
                label="Search" 
                value={d.trafficSources?.search || 0} 
                max={d.totalSessions || 1}
                color="#10b981"
              />
              <ProgressBar 
                label="Social" 
                value={d.trafficSources?.social || 0} 
                max={d.totalSessions || 1}
                color="#f59e0b"
              />
              <ProgressBar 
                label="Referral" 
                value={d.trafficSources?.referral || 0} 
                max={d.totalSessions || 1}
                color="#8b5cf6"
              />
            </div>
          </div>
        </div>

        <div style={styles.gridWide}>
          {/* Top Countries */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🌍 Top Countries <span style={styles.cardSubtitle}>(click to drill down)</span></h3>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Country</th>
                  <th style={styles.th}>Events</th>
                  <th style={styles.th}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(d.byCountry || {}).slice(0, 10).map(([country, count]) => (
                  <tr 
                    key={country} 
                    style={styles.clickableRow}
                    onClick={() => fetchDrillDown('country', { code: country })}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={styles.td}>{country}</td>
                    <td style={styles.td}>{count.toLocaleString()}</td>
                    <td style={styles.td}>{((count / d.totalEvents) * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Device Breakdown */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📱 Device Breakdown</h3>
            <div style={{ marginTop: '20px' }}>
              {Object.entries(d.byDevice || {}).map(([device, count]) => (
                <ProgressBar 
                  key={device}
                  label={device} 
                  value={count} 
                  max={d.totalSessions || 1}
                  color={device === 'Desktop' ? '#3b82f6' : device === 'Mobile' ? '#10b981' : '#f59e0b'}
                />
              ))}
            </div>
            
            <h4 style={{ ...styles.cardTitle, marginTop: '24px', fontSize: '14px' }}>🌐 Browsers</h4>
            <div style={{ marginTop: '12px' }}>
              {Object.entries(d.byBrowser || {}).slice(0, 5).map(([browser, count]) => (
                <ProgressBar 
                  key={browser}
                  label={browser} 
                  value={count} 
                  max={d.totalSessions || 1}
                  color="#8b5cf6"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Event Types */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📋 Events by Type</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '16px' }}>
            {Object.entries(d.byEventType || {}).slice(0, 12).map(([type, count]) => (
              <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: 'rgba(15, 23, 42, 0.5)', borderRadius: '6px' }}>
                <span style={{ fontSize: '13px' }}>{type.replace(/_/g, ' ')}</span>
                <span style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600' }}>{count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  const renderRealtime = () => {
    if (!realtimeData) return <div style={styles.loading}>Loading realtime data...</div>;
    
    const r = realtimeData;
    
    return (
      <>
        <div style={{ ...styles.card, marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={styles.liveIndicator}>
            <div style={styles.liveDot} />
            <span>LIVE</span>
          </div>
          <span style={{ color: '#64748b' }}>Auto-refreshing every 10 seconds</span>
          <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '12px' }}>
            Last update: {new Date(r.timestamp).toLocaleTimeString()}
          </span>
        </div>

        <div style={styles.grid}>
          <MetricCard 
            icon="🟢" 
            label="Active Sessions" 
            value={r.activeSessions}
            color="#22c55e" 
          />
          <MetricCard 
            icon="👥" 
            label="Active Visitors" 
            value={r.activeVisitors}
            color="#3b82f6" 
          />
        </div>

        <div style={styles.gridWide}>
          {/* Live Feed */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📡 Live Activity Feed</h3>
            <div style={{ maxHeight: '400px', overflow: 'auto', marginTop: '16px' }}>
              {r.recentEvents?.map((event, i) => (
                <div key={i} style={{ 
                  padding: '12px', 
                  borderBottom: '1px solid #1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Badge type={event.type.includes('error') ? 'danger' : 'info'}>
                    {event.type}
                  </Badge>
                  <span style={{ flex: 1, fontSize: '13px' }}>{event.page || '-'}</span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    {event.city}, {event.country}
                  </span>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Active Sessions */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🖥️ Active Sessions</h3>
            <div style={{ maxHeight: '400px', overflow: 'auto', marginTop: '16px' }}>
              {r.sessions?.map((session, i) => (
                <div 
                  key={i} 
                  style={{ 
                    padding: '12px', 
                    borderBottom: '1px solid #1e293b',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchDrillDown('session', { sessionId: session.id })}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px' }}>{session.city}, {session.country}</span>
                    <Badge type="success">{session.device}</Badge>
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {session.browser} • {session.pageViews} pages • Started {new Date(session.startedAt).toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderGeography = () => {
    if (!geoData) return <div style={styles.loading}>Loading geographic data...</div>;
    
    const g = geoData;
    
    return (
      <>
        <div style={styles.grid}>
          <MetricCard icon="🌍" label="Countries" value={g.totalCountries} color="#3b82f6" />
          <MetricCard icon="🏙️" label="Cities" value={g.totalCities} color="#10b981" />
          <MetricCard icon="📍" label="Total Events" value={g.totalEvents?.toLocaleString()} color="#f59e0b" />
        </div>

        <div style={styles.gridWide}>
          {/* Countries Table */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🌍 Countries <span style={styles.cardSubtitle}>(click for details)</span></h3>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Country</th>
                  <th style={styles.th}>Events</th>
                  <th style={styles.th}>Users</th>
                  <th style={styles.th}>%</th>
                </tr>
              </thead>
              <tbody>
                {g.countries?.slice(0, 15).map((country) => (
                  <tr 
                    key={country.name}
                    style={styles.clickableRow}
                    onClick={() => fetchDrillDown('country', { code: country.name })}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={styles.td}>
                      <span style={{ marginRight: '8px' }}>{getCountryFlag(country.code)}</span>
                      {country.name}
                    </td>
                    <td style={styles.td}>{country.events.toLocaleString()}</td>
                    <td style={styles.td}>{country.uniqueUsers}</td>
                    <td style={styles.td}>{country.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Top Cities */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🏙️ Top Cities</h3>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>City</th>
                  <th style={styles.th}>Region</th>
                  <th style={styles.th}>Country</th>
                  <th style={styles.th}>Events</th>
                </tr>
              </thead>
              <tbody>
                {g.cities?.slice(0, 15).map((city, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{city.city}</td>
                    <td style={styles.td}>{city.region || '-'}</td>
                    <td style={styles.td}>{city.country}</td>
                    <td style={styles.td}>{city.count?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderUsers = () => {
    const u = usersData;
    
    return (
      <>
        <div style={{ ...styles.card, marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <input 
              type="text"
              placeholder="Search users by email or name..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={styles.searchInput}
            />
            <span style={{ color: '#64748b', fontSize: '14px' }}>
              {u.total?.toLocaleString() || 0} total users
            </span>
          </div>
        </div>

        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Balance</th>
                <th style={styles.th}>Conversations</th>
                <th style={styles.th}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {u.users?.map((user) => (
                <tr 
                  key={user.user_id}
                  style={styles.clickableRow}
                  onClick={() => fetchDrillDown('user', { userId: user.user_id })}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={styles.td}>{user.email}</td>
                  <td style={styles.td}>{user.name || '-'}</td>
                  <td style={styles.td}>
                    <Badge type={user.role === 'admin' ? 'warning' : 'info'}>{user.role}</Badge>
                  </td>
                  <td style={styles.td}>{user.units_balance || 0} units</td>
                  <td style={styles.td}>{user.conversationCount || 0}</td>
                  <td style={styles.td}>{new Date(user.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <Pagination 
            page={u.page || 1}
            totalPages={u.totalPages || 1}
            onPageChange={(p) => fetchUsers(p, userSearch)}
          />
        </div>
      </>
    );
  };

  const renderSessions = () => {
    const s = sessionsData;
    
    return (
      <>
        <div style={{ ...styles.card, marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <select 
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
              style={styles.select}
            >
              <option value="">All Sessions</option>
              <option value="active">Active Only</option>
              <option value="bounced">Bounced</option>
              <option value="engaged">Engaged</option>
            </select>
            <span style={{ color: '#64748b', fontSize: '14px' }}>
              {s.total?.toLocaleString() || 0} sessions
            </span>
          </div>
        </div>

        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Location</th>
                <th style={styles.th}>Device</th>
                <th style={styles.th}>Pages</th>
                <th style={styles.th}>Duration</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Started</th>
              </tr>
            </thead>
            <tbody>
              {s.sessions?.map((session) => (
                <tr 
                  key={session.session_id}
                  style={styles.clickableRow}
                  onClick={() => fetchDrillDown('session', { sessionId: session.session_id })}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={styles.td}>{session.city}, {session.country}</td>
                  <td style={styles.td}>{session.device_type} / {session.browser}</td>
                  <td style={styles.td}>{session.page_views || 0}</td>
                  <td style={styles.td}>{session.durationFormatted || '-'}</td>
                  <td style={styles.td}>
                    {session.is_active ? (
                      <Badge type="success">Active</Badge>
                    ) : session.bounce ? (
                      <Badge type="warning">Bounced</Badge>
                    ) : session.engaged ? (
                      <Badge type="info">Engaged</Badge>
                    ) : (
                      <Badge type="info">Ended</Badge>
                    )}
                  </td>
                  <td style={styles.td}>{new Date(session.started_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <Pagination 
            page={s.page || 1}
            totalPages={s.totalPages || 1}
            onPageChange={(p) => fetchSessions(p, sessionFilter)}
          />
        </div>
      </>
    );
  };

  const renderAiUsage = () => {
    if (!aiUsageData) return <div style={styles.loading}>Loading AI usage data...</div>;
    
    const a = aiUsageData;
    
    return (
      <>
        <div style={styles.grid}>
          <MetricCard icon="🤖" label="Total AI Requests" value={a.totalRequests?.toLocaleString()} color="#8b5cf6" />
          <MetricCard icon="🤝" label="Consensus Generated" value={a.consensusGenerated} color="#3b82f6" />
          <MetricCard icon="📝" label="Summaries Generated" value={a.summariesGenerated} color="#10b981" />
          <MetricCard icon="⚠️" label="Total Errors" value={a.totalErrors} color="#ef4444" />
        </div>

        <div style={styles.gridWide}>
          {/* AI Model Usage */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🤖 AI Model Usage</h3>
            <div style={{ marginTop: '20px' }}>
              <ProgressBar label="Claude" value={a.byModel?.claude || 0} max={a.totalRequests || 1} color="#f97316" />
              <ProgressBar label="GPT" value={a.byModel?.gpt || 0} max={a.totalRequests || 1} color="#22c55e" />
              <ProgressBar label="Grok" value={a.byModel?.grok || 0} max={a.totalRequests || 1} color="#3b82f6" />
              <ProgressBar label="DeepSeek" value={a.byModel?.deepseek || 0} max={a.totalRequests || 1} color="#8b5cf6" />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginTop: '24px' }}>
              {['claude', 'gpt', 'grok', 'deepseek'].map(model => (
                <div key={model} style={{ textAlign: 'center', padding: '16px', backgroundColor: 'rgba(15, 23, 42, 0.5)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700' }}>{a.modelPercentages?.[model] || 0}%</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>{model}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Usage Trend */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📈 Daily Usage Trend</h3>
            <BarChart 
              data={a.dailyTrend || []}
              labelKey="date"
              valueKey="claude"
              color="#8b5cf6"
            />
          </div>
        </div>

        {/* Error Breakdown */}
        {a.totalErrors > 0 && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>⚠️ AI Errors by Model</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginTop: '16px' }}>
              {['claude', 'gpt', 'grok', 'deepseek'].map(model => (
                <div key={model} style={{ 
                  textAlign: 'center', 
                  padding: '16px', 
                  backgroundColor: a.errors?.[model] > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(15, 23, 42, 0.5)',
                  borderRadius: '8px',
                  border: a.errors?.[model] > 0 ? '1px solid #ef4444' : '1px solid transparent'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: a.errors?.[model] > 0 ? '#ef4444' : '#22c55e' }}>
                    {a.errors?.[model] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>{model} errors</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderErrors = () => {
    if (!errorsData) return <div style={styles.loading}>Loading error data...</div>;
    
    const e = errorsData;
    
    return (
      <>
        <div style={styles.grid}>
          <MetricCard icon="⚠️" label="Total Errors" value={e.totalErrors} color="#ef4444" />
        </div>

        <div style={styles.gridWide}>
          {/* Error Types */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📊 Errors by Type</h3>
            <div style={{ marginTop: '20px' }}>
              {Object.entries(e.byType || {}).map(([type, count]) => (
                <ProgressBar key={type} label={type} value={count} max={e.totalErrors || 1} color="#ef4444" />
              ))}
            </div>
          </div>

          {/* Top Errors */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🔥 Top Error Messages</h3>
            <div style={{ marginTop: '16px' }}>
              {e.topErrors?.map((err, i) => (
                <div key={i} style={{ 
                  padding: '12px', 
                  borderBottom: '1px solid #1e293b',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '13px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {err.message}
                  </span>
                  <Badge type="danger">{err.count}</Badge>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Errors */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>🕐 Recent Errors</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Time</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Message</th>
                <th style={styles.th}>Page</th>
                <th style={styles.th}>Browser</th>
              </tr>
            </thead>
            <tbody>
              {e.recentErrors?.slice(0, 15).map((error) => (
                <tr key={error.id}>
                  <td style={styles.td}>{new Date(error.timestamp).toLocaleString()}</td>
                  <td style={styles.td}><Badge type="danger">{error.type}</Badge></td>
                  <td style={{ ...styles.td, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {error.message || '-'}
                  </td>
                  <td style={styles.td}>{error.page || '-'}</td>
                  <td style={styles.td}>{error.browser || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderSearches = () => {
    if (!searchesData) return <div style={styles.loading}>Loading search data...</div>;
    
    const s = searchesData;
    
    return (
      <>
        <div style={styles.grid}>
          <MetricCard icon="🔍" label="Total Searches" value={s.totalSearches?.toLocaleString()} color="#3b82f6" />
          <MetricCard icon="📝" label="Unique Terms" value={s.uniqueTerms} color="#10b981" />
          <MetricCard icon="📊" label="Avg Results" value={s.avgResultsCount} color="#f59e0b" />
          <MetricCard icon="❌" label="Zero Results Rate" value={`${s.zeroResultRate}%`} color="#ef4444" />
        </div>

        <div style={styles.gridWide}>
          {/* Top Searches */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🔥 Top Search Terms</h3>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Search Term</th>
                  <th style={styles.th}>Count</th>
                </tr>
              </thead>
              <tbody>
                {s.topSearches?.map((search, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{search.term}</td>
                    <td style={styles.td}>{search.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Zero Results */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>❌ Zero Result Searches</h3>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
              These searches returned no results - consider adding this content
            </p>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Search Term</th>
                  <th style={styles.th}>Count</th>
                </tr>
              </thead>
              <tbody>
                {s.zeroResultSearches?.map((search, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{search.term}</td>
                    <td style={styles.td}><Badge type="warning">{search.count}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  // Drill-down modal content
  const renderDrillModal = () => {
    if (!drillModal || !drillData) return null;

    let content;
    let title;

    switch (drillModal) {
      case 'country':
        title = `${drillData.country} Details`;
        content = (
          <>
            <div style={styles.grid}>
              <MetricCard icon="📍" label="Total Events" value={drillData.totalEvents?.toLocaleString()} color="#3b82f6" />
              <MetricCard icon="👥" label="Unique Users" value={drillData.uniqueUsers} color="#10b981" />
            </div>
            
            <h4 style={{ marginTop: '24px', marginBottom: '16px' }}>🏙️ Cities</h4>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>City</th>
                  <th style={styles.th}>Region</th>
                  <th style={styles.th}>Events</th>
                </tr>
              </thead>
              <tbody>
                {drillData.cities?.slice(0, 15).map((city, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{city.name}</td>
                    <td style={styles.td}>{city.region || '-'}</td>
                    <td style={styles.td}>{city.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        );
        break;

      case 'user':
        title = `User: ${drillData.user?.email}`;
        content = (
          <>
            <div style={styles.grid}>
              <MetricCard icon="💬" label="Conversations" value={drillData.stats?.totalConversations || 0} color="#8b5cf6" />
              <MetricCard icon="📊" label="Events" value={drillData.stats?.totalEvents || 0} color="#3b82f6" />
              <MetricCard icon="💰" label="Balance" value={`${drillData.user?.units_balance || 0} units`} color="#10b981" />
            </div>

            <h4 style={{ marginTop: '24px', marginBottom: '16px' }}>📍 Locations Used</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {drillData.stats?.locations?.map((loc, i) => (
                <Badge key={i} type="info">{loc}</Badge>
              ))}
            </div>

            <h4 style={{ marginTop: '24px', marginBottom: '16px' }}>🖥️ Devices Used</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {drillData.stats?.devices?.map((dev, i) => (
                <Badge key={i} type="info">{dev}</Badge>
              ))}
            </div>

            <h4 style={{ marginTop: '24px', marginBottom: '16px' }}>🕐 Recent Activity</h4>
            <div style={styles.timeline}>
              {drillData.recentEvents?.slice(0, 10).map((event, i) => (
                <div key={i} style={styles.timelineItem}>
                  <div style={styles.timelineDot} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{event.event_type}</span>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      {new Date(event.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>{event.page_path}</div>
                </div>
              ))}
            </div>
          </>
        );
        break;

      case 'session':
        title = `Session Details`;
        content = (
          <>
            <div style={styles.grid}>
              <MetricCard icon="📄" label="Page Views" value={drillData.summary?.pageViews || 0} color="#3b82f6" />
              <MetricCard icon="⏱️" label="Duration" value={drillData.session?.durationFormatted || '-'} color="#10b981" />
              <MetricCard icon="🔗" label="Events" value={drillData.summary?.totalEvents || 0} color="#f59e0b" />
            </div>

            <div style={{ ...styles.card, marginTop: '24px' }}>
              <h4>📍 Session Info</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginTop: '16px' }}>
                <div><strong>Location:</strong> {drillData.session?.city}, {drillData.session?.region}, {drillData.session?.country}</div>
                <div><strong>Device:</strong> {drillData.session?.device_type}</div>
                <div><strong>Browser:</strong> {drillData.session?.browser}</div>
                <div><strong>OS:</strong> {drillData.session?.os}</div>
                <div><strong>IP:</strong> {drillData.session?.ip_address}</div>
                <div><strong>Referrer:</strong> {drillData.session?.referrer || 'Direct'}</div>
              </div>
            </div>

            <h4 style={{ marginTop: '24px', marginBottom: '16px' }}>🗺️ User Journey</h4>
            <div style={styles.timeline}>
              {drillData.journey?.map((step, i) => (
                <div key={i} style={styles.timelineItem}>
                  <div style={styles.timelineDot} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Badge type="info">{step.type}</Badge>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      +{step.timeFromStart}s
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', marginTop: '4px' }}>{step.page || '-'}</div>
                </div>
              ))}
            </div>
          </>
        );
        break;

      default:
        content = <div>Unknown drill-down type</div>;
    }

    return (
      <div style={styles.modal} onClick={() => setDrillModal(null)}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <button style={styles.modalClose} onClick={() => setDrillModal(null)}>×</button>
          <h2 style={{ marginBottom: '24px' }}>{title}</h2>
          {content}
        </div>
      </div>
    );
  };

  // Helper function for country flags
  const getCountryFlag = (code) => {
    if (!code || code.length !== 2) return '🌍';
    const codePoints = code.toUpperCase().split('').map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  if (loading && !dashboardData) {
    return <div style={styles.loading}>⏳ Loading admin dashboard...</div>;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>⚠️ {error}</div>
        <button style={styles.button} onClick={fetchDashboard}>Try Again</button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: '📊 Overview' },
    { id: 'realtime', label: '🟢 Realtime' },
    { id: 'geography', label: '🌍 Geography' },
    { id: 'users', label: '👥 Users' },
    { id: 'sessions', label: '🖥️ Sessions' },
    { id: 'ai', label: '🤖 AI Usage' },
    { id: 'errors', label: '⚠️ Errors' },
    { id: 'searches', label: '🔍 Searches' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Admin Analytics Dashboard</h1>
        <div style={styles.controls}>
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(Number(e.target.value))} 
            style={styles.select}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={autoRefresh} 
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span style={{ fontSize: '14px', color: '#94a3b8' }}>Auto-refresh</span>
          </label>

          <div style={styles.exportMenu}>
            <button 
              style={styles.buttonSecondary}
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
            >
              📥 Export
            </button>
            {exportMenuOpen && (
              <div style={styles.exportDropdown}>
                <div style={styles.exportOption} onClick={() => handleExport('sessions', 'csv')}>
                  📊 Sessions (CSV)
                </div>
                <div style={styles.exportOption} onClick={() => handleExport('events', 'csv')}>
                  📋 Events (CSV)
                </div>
                <div style={styles.exportOption} onClick={() => handleExport('users', 'csv')}>
                  👥 Users (CSV)
                </div>
                <div style={styles.exportOption} onClick={() => handleExport('sessions', 'json')}>
                  📊 Sessions (JSON)
                </div>
                <div style={styles.exportOption} onClick={() => handleExport('events', 'json')}>
                  📋 Events (JSON)
                </div>
              </div>
            )}
          </div>
          
          <button style={styles.button} onClick={fetchDashboard}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div style={styles.tabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.content}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'realtime' && renderRealtime()}
        {activeTab === 'geography' && renderGeography()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'sessions' && renderSessions()}
        {activeTab === 'ai' && renderAiUsage()}
        {activeTab === 'errors' && renderErrors()}
        {activeTab === 'searches' && renderSearches()}
      </div>

      {renderDrillModal()}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
